import React, { useEffect, useState, useCallback } from 'react';
import {
  KeyRound,
  LogOut,
  Loader2,
  Users,
  Building2,
  Coins,
  ScrollText,
  RefreshCw,
  Sparkles,
} from 'lucide-react';
import {
  sovereignVerify,
  sovereignWhoami,
  sovereignSnapshot,
  sovereignSignOut,
  getSovereignToken,
} from '../../lib/sovereign';

/**
 * /admin/sovereign-dashboard — the founder's single source of truth.
 *
 * Dark atelier surface. One passphrase opens the gate. Four cards inside:
 * Guardian Milestone · Partner Feed · Dharma Ledger · Token Pulse.
 */

const fmtUSD = (n) =>
  (n || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });

const fmtInt = (n) => (n || 0).toLocaleString('en-US');

// ============================================================================
// Sign-in handshake
// ============================================================================
const Handshake = ({ onUnlocked }) => {
  const [phrase, setPhrase] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!phrase.trim()) return;
    setBusy(true);
    try {
      await sovereignVerify(phrase.trim());
      onUnlocked();
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      setErr(typeof detail === 'string' ? detail : 'The phrase does not open the gate.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="sovereign-handshake"
      className="min-h-screen flex items-center justify-center px-6 py-12 bg-[#0E1A22] text-[#F4F8F9] relative overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.07] pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(circle at 22% 18%, #C1D7E2 0px, transparent 1px), radial-gradient(circle at 78% 42%, #AAC6D4 0px, transparent 1.4px), radial-gradient(circle at 53% 84%, #7C9CB1 0px, transparent 1.1px)',
          backgroundSize: '340px 340px',
        }}
      />

      <form
        onSubmit={submit}
        className="relative w-full max-w-md bg-[#F4F8F9]/[0.04] border border-[#F4F8F9]/25 backdrop-blur-sm p-10 md:p-12"
      >
        <span
          aria-hidden
          className="absolute top-0 left-10 right-10 h-px"
          style={{ background: 'linear-gradient(90deg, transparent, #D4AF5A 35%, #D4AF5A 65%, transparent)' }}
        />
        <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-[#F4F8F9]/70 mb-2">
          <span className="inline-block w-7 h-px bg-[#F4F8F9]/70 mr-3 align-middle" />
          The Sovereign Gate
        </p>
        <h1 className="font-serif text-3xl md:text-4xl font-light leading-tight mb-3">
          Dhanvantri's Witness.
        </h1>
        <p className="font-serif italic text-[#F4F8F9]/85 leading-relaxed mb-10">
          A single phrase opens the four ledgers of the house.
        </p>

        <label className="block">
          <span className="font-mono text-[9px] tracking-[0.3em] uppercase text-[#F4F8F9]/60">
            Passphrase
          </span>
          <input
            type="password"
            value={phrase}
            onChange={(e) => setPhrase(e.target.value)}
            placeholder="—"
            autoFocus
            data-testid="sovereign-passphrase-input"
            className="mt-2 w-full bg-transparent border-b border-[#F4F8F9]/30 focus:border-[#F4F8F9] py-2 font-serif text-lg text-[#F4F8F9] placeholder-[#F4F8F9]/40 focus:outline-none"
          />
        </label>

        <button
          type="submit"
          disabled={busy || !phrase.trim()}
          data-testid="sovereign-handshake-submit"
          className="mt-8 w-full inline-flex items-center justify-center gap-3 px-7 py-4 text-[10px] tracking-[0.28em] uppercase font-medium text-[#0E1A22] bg-[#F4F8F9] hover:bg-[#C1D7E2] disabled:opacity-40 transition-colors"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} strokeWidth={1.6} />}
          {busy ? 'Verifying the seal…' : 'Open the gate'}
        </button>

        {err && (
          <p
            data-testid="sovereign-handshake-error"
            className="mt-5 font-mono text-[11px] text-[#E8A693]"
          >
            {err}
          </p>
        )}

        <p className="mt-8 font-mono text-[9px] tracking-[0.26em] uppercase text-[#F4F8F9]/45 leading-relaxed">
          For the founder only. Five wrong handshakes triggers a 15-minute silence.
        </p>
      </form>
    </div>
  );
};

// ============================================================================
// Metric card — atelier glass on the dark field
// ============================================================================
const Card = ({ icon: Icon, title, testId, children, accent = false }) => (
  <article
    data-testid={testId}
    className={`relative border p-7 md:p-8 ${
      accent
        ? 'bg-[#F4F8F9] text-[#0E1A22] border-[#F4F8F9]'
        : 'bg-[#F4F8F9]/[0.04] text-[#F4F8F9] border-[#F4F8F9]/25 backdrop-blur-sm'
    }`}
  >
    <span
      aria-hidden
      className="absolute top-0 left-7 right-7 h-px"
      style={{ background: 'linear-gradient(90deg, transparent, #D4AF5A 35%, #D4AF5A 65%, transparent)' }}
    />
    <p
      className={`flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase mb-5 ${
        accent ? 'text-[#7C9CB1]' : 'text-[#F4F8F9]/70'
      }`}
    >
      <Icon size={13} strokeWidth={1.5} /> {title}
    </p>
    {children}
  </article>
);

const Bar = ({ pct, dark = false }) => (
  <div
    className={`h-px ${dark ? 'bg-[#0E1A22]/15' : 'bg-[#F4F8F9]/20'} mt-3`}
    role="progressbar"
    aria-valuenow={pct}
    aria-valuemin={0}
    aria-valuemax={100}
  >
    <div
      className={`h-px transition-all duration-700 ${dark ? 'bg-[#0E1A22]' : 'bg-[#F4F8F9]'}`}
      style={{ width: `${Math.max(0.5, Math.min(100, pct))}%` }}
    />
  </div>
);

// ============================================================================
// Snapshot view — the four ledgers
// ============================================================================
const Snapshot = ({ data, onSignOut, onRefresh, refreshing }) => {
  const g = data.guardians;
  const p = data.partner_leads;
  const l = data.dharma_ledger;
  const t = data.token_pulse;
  const asOf = (data.as_of || '').slice(0, 19).replace('T', ' ');

  return (
    <div
      data-testid="sovereign-dashboard"
      className="min-h-screen bg-[#0E1A22] text-[#F4F8F9] px-6 md:px-12 py-12 md:py-16 relative overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(circle at 22% 18%, #C1D7E2 0px, transparent 1px), radial-gradient(circle at 78% 42%, #AAC6D4 0px, transparent 1.4px)',
          backgroundSize: '420px 420px',
        }}
      />

      <div className="relative max-w-[1400px] mx-auto">
        {/* Header */}
        <header className="flex items-start justify-between gap-6 flex-wrap mb-12 md:mb-16">
          <div>
            <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-[#F4F8F9]/70 mb-3">
              <span className="inline-block w-7 h-px bg-[#F4F8F9]/70 mr-3 align-middle" />
              The Sovereign Dashboard · Dhanvantri's Witness
            </p>
            <h1 className="font-serif text-4xl md:text-5xl font-light leading-[1.05]">
              The four ledgers,
              <br />
              <em className="text-[#F4F8F9]/80">witnessed at once</em>.
            </h1>
            <p
              data-testid="sovereign-narrative"
              className="mt-5 font-serif italic text-[#F4F8F9]/85 max-w-2xl leading-relaxed"
            >
              {data.house?.narrative}
            </p>
          </div>
          <div className="flex flex-col items-end gap-3">
            <p className="font-mono text-[10px] tracking-[0.26em] uppercase text-[#F4F8F9]/60">
              As of · {asOf} UTC
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onRefresh}
                data-testid="sovereign-refresh"
                className="inline-flex items-center gap-2 px-4 py-2 text-[10px] tracking-[0.22em] uppercase border border-[#F4F8F9]/30 hover:bg-[#F4F8F9]/10"
              >
                <RefreshCw size={11} strokeWidth={1.6} className={refreshing ? 'animate-spin' : ''} />
                Refresh
              </button>
              <button
                type="button"
                onClick={onSignOut}
                data-testid="sovereign-signout"
                className="inline-flex items-center gap-2 px-4 py-2 text-[10px] tracking-[0.22em] uppercase border border-[#F4F8F9]/30 hover:bg-[#F4F8F9]/10"
              >
                <LogOut size={11} strokeWidth={1.6} />
                Close gate
              </button>
            </div>
          </div>
        </header>

        {/* Four primary metric cards */}
        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 mb-10 md:mb-14">
          {/* Guardian milestone */}
          <Card icon={Users} title="Guardians · live count" testId="card-guardians">
            <p className="font-serif text-4xl md:text-5xl font-light leading-none">
              {fmtInt(g.total)}
              <span className="font-mono text-[12px] tracking-[0.18em] text-[#F4F8F9]/65 ml-2">
                / {fmtInt(g.founding_cap)}
              </span>
            </p>
            <Bar pct={g.founding_pct} />
            <p className="mt-3 font-mono text-[10px] tracking-[0.22em] uppercase text-[#F4F8F9]/70">
              {g.founding_pct}% of the founding circle · {fmtInt(g.seats_remaining)} seats remain
            </p>
          </Card>

          {/* Hotel partner leads */}
          <Card icon={Building2} title="Hotel leads" testId="card-partner-leads">
            <p className="font-serif text-4xl md:text-5xl font-light leading-none">
              {fmtInt(p.total)}
            </p>
            <Bar pct={Math.min(100, p.total * 5)} />
            <p className="mt-3 font-mono text-[10px] tracking-[0.22em] uppercase text-[#F4F8F9]/70">
              Houses past the Oracle gate
            </p>
          </Card>

          {/* Dharma Ledger — 25 % markup collected */}
          <Card icon={ScrollText} title="Dharma Ledger · 25 % markup" testId="card-dharma">
            <p className="font-serif text-4xl md:text-5xl font-light leading-none">
              {fmtUSD(l.markup_collected)}
            </p>
            <p className="mt-3 font-mono text-[10px] tracking-[0.22em] uppercase text-[#F4F8F9]/70">
              On {fmtInt(l.orders)} witnessed orders
            </p>
            <ul className="mt-4 space-y-1 font-mono text-[11px] text-[#F4F8F9]/80">
              <li className="flex justify-between border-b border-[#F4F8F9]/15 pb-1">
                <span>Revenue</span><span>{fmtUSD(l.revenue)}</span>
              </li>
              <li className="flex justify-between">
                <span>Audited cost</span><span>{fmtUSD(l.audited_cost)}</span>
              </li>
            </ul>
          </Card>

          {/* Token Pulse */}
          <Card icon={Coins} title="Token Pulse · $SEVA" testId="card-token-pulse" accent>
            <p className="font-serif text-4xl md:text-5xl font-light leading-none">
              {fmtInt(t.in_circulation)}
              <span className="font-mono text-[12px] tracking-[0.18em] text-[#0E1A22]/60 ml-2">
                / {fmtInt(t.total_supply)}
              </span>
            </p>
            <Bar pct={Math.max(0.5, t.circulation_pct * 100)} dark />
            <p className="mt-3 font-mono text-[10px] tracking-[0.22em] uppercase text-[#7C9CB1]">
              {t.circulation_pct.toFixed(4)}% of supply in circulation
            </p>
            <ul className="mt-4 space-y-1 font-mono text-[11px] text-[#0E1A22]/85">
              <li className="flex justify-between border-b border-[#0E1A22]/10 pb-1">
                <span>Guardians</span><span>{fmtInt(t.breakdown.guardians)}</span>
              </li>
              <li className="flex justify-between border-b border-[#0E1A22]/10 pb-1">
                <span>Sevagram</span><span>{fmtInt(t.breakdown.sevagram)}</span>
              </li>
              <li className="flex justify-between">
                <span>Orders</span><span>{fmtInt(t.breakdown.orders)}</span>
              </li>
            </ul>
          </Card>
        </section>

        {/* Partner Feed */}
        <section
          data-testid="sovereign-partner-feed"
          className="border border-[#F4F8F9]/25 p-7 md:p-8 mb-10"
        >
          <p className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F8F9]/70 mb-6">
            <Sparkles size={13} strokeWidth={1.5} />
            Partner feed · houses past the gate
          </p>
          {p.recent?.length ? (
            <ul className="space-y-3 font-mono text-[12px] text-[#F4F8F9]/90">
              {p.recent.map((row, i) => (
                <li
                  key={`${row.email}-${i}`}
                  data-testid={`partner-row-${i}`}
                  className="flex items-baseline justify-between gap-6 border-b border-[#F4F8F9]/15 pb-2 last:border-b-0 last:pb-0"
                >
                  <span className="flex flex-col">
                    <span className="text-[14px] font-serif italic text-[#F4F8F9]">
                      {row.house || '—'}
                    </span>
                    <span className="text-[10px] tracking-[0.22em] uppercase text-[#F4F8F9]/60">
                      {row.email}
                    </span>
                  </span>
                  <span className="text-[10px] tracking-[0.22em] uppercase text-[#F4F8F9]/60">
                    {(row.created_at || '').slice(0, 10)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="font-serif italic text-[#F4F8F9]/65 leading-relaxed">
              No hotelier has named themselves yet. The first arrival will surface here.
            </p>
          )}
        </section>

        {/* Recent Guardians strip */}
        <section
          data-testid="sovereign-recent-guardians"
          className="border border-[#F4F8F9]/25 p-7 md:p-8"
        >
          <p className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F8F9]/70 mb-6">
            <Users size={13} strokeWidth={1.5} />
            Recent inscriptions
          </p>
          {g.recent?.length ? (
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3 font-mono text-[12px] text-[#F4F8F9]/90">
              {g.recent.map((row, i) => (
                <li
                  key={`${row.email}-${i}`}
                  data-testid={`guardian-row-${i}`}
                  className="flex items-baseline justify-between border-b border-[#F4F8F9]/15 pb-2"
                >
                  <span>
                    #{row.guardian_number} &nbsp;·&nbsp; {row.email}
                  </span>
                  <span className="text-[10px] tracking-[0.2em] uppercase text-[#F4F8F9]/60">
                    {(row.created_at || '').slice(0, 10)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="font-serif italic text-[#F4F8F9]/65 leading-relaxed">
              The Circle is empty. The first Guardian will appear here.
            </p>
          )}
        </section>
      </div>
    </div>
  );
};

// ============================================================================
// Page shell
// ============================================================================
const SovereignDashboardPage = () => {
  const [unlocked, setUnlocked] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [fatal, setFatal] = useState('');

  const load = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true);
    try {
      const data = await sovereignSnapshot();
      setSnapshot(data);
      setFatal('');
    } catch (e) {
      if (e?.response?.status === 401) {
        // token expired or invalid → flush
        sovereignSignOut();
        setUnlocked(false);
      } else {
        setFatal('Could not load the sovereign snapshot.');
      }
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, []);

  // On mount — if we already have a token, validate + load
  useEffect(() => {
    const t = getSovereignToken();
    if (!t) { setLoading(false); return; }
    (async () => {
      try {
        await sovereignWhoami();
        setUnlocked(true);
        await load();
      } catch {
        sovereignSignOut();
        setLoading(false);
      }
    })();
  }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0E1A22] flex items-center justify-center">
        <Loader2 size={20} className="animate-spin text-[#F4F8F9]" />
      </div>
    );
  }

  if (!unlocked) {
    return (
      <Handshake
        onUnlocked={async () => {
          setUnlocked(true);
          setLoading(true);
          await load();
        }}
      />
    );
  }

  if (!snapshot) {
    return (
      <div
        data-testid="sovereign-fatal"
        className="min-h-screen bg-[#0E1A22] text-[#F4F8F9] flex items-center justify-center px-6"
      >
        <p className="font-serif italic">{fatal || 'No snapshot loaded.'}</p>
      </div>
    );
  }

  return (
    <Snapshot
      data={snapshot}
      onRefresh={() => load(true)}
      refreshing={refreshing}
      onSignOut={() => {
        sovereignSignOut();
        setUnlocked(false);
        setSnapshot(null);
      }}
    />
  );
};

export default SovereignDashboardPage;
